//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { FileUploaderAssetFailure } from '@campaigns/components/shared/file-uploader/FileUploaderTypes';
import {
  getFileSizeAndType,
  isSupportedMimeType,
} from '@campaigns/components/shared/file-uploader/utils';
import { SupportedMimeTypes } from '@utils/mimeTypes';

export const uploadFiles = async ({
  files,
  existingAssetUrls,
  shouldAcceptMultipleFiles,
  maxNumberOfFiles,
  maxTotalSize,
  supportedFileTypes,
}: {
  files: FileList | null;
  existingAssetUrls: string[];
  shouldAcceptMultipleFiles: boolean;
  maxNumberOfFiles?: number;
  maxTotalSize: number;
  supportedFileTypes: SupportedMimeTypes[];
}) => {
  if (!files?.length) {
    throw new Error('No files selected.');
  }

  const failureReasons: FileUploaderAssetFailure = {};

  const selectedFiles = Array.from(files);

  if (!shouldAcceptMultipleFiles && selectedFiles.length > 1) {
    failureReasons.isCountTooMany = true;
    return { failureReasons, files: [] };
  }

  if (
    shouldAcceptMultipleFiles &&
    maxNumberOfFiles &&
    selectedFiles.length + existingAssetUrls.length > maxNumberOfFiles
  ) {
    failureReasons.isCountTooMany = true;
  }

  const existingImageSizesAndTypes = await Promise.all(
    existingAssetUrls.map(async (url) => getFileSizeAndType(url)),
  );

  const totalSize = [...selectedFiles, ...existingImageSizesAndTypes].reduce(
    (sum, file) => sum + file.size,
    0,
  );

  if (totalSize > maxTotalSize) {
    failureReasons.isSizeTooLarge = true;
  }

  for (const file of selectedFiles) {
    if (!isSupportedMimeType(file.type, supportedFileTypes)) {
      failureReasons.isUnsupportedFilePresent = true;
      break;
    }
  }

  return { failureReasons, files: selectedFiles };
};
